import { Plus } from "lucide-react";
import { useContext, useEffect, useState } from "react";
import { useCookies } from "react-cookie";
import { useNavigate } from "react-router-dom";

import { Button } from "@/app/components/ui/button";
import type { PersonalLink, ResumeContextValue } from "@/app/types/resume";

import tips from "../../../../assets/copy/tips";
import ErrorToast from "../../../../components/ErrorToast";
import Spinner from "../../../../components/Spinner";
import Tips from "../../../../components/Tips";
import ResumeContext from "../../../../context/ResumeContext";
import { personalLinkRowSchema } from "../../../../schemas/resume/personalLinks";
import { StaticValue } from "../../../../seed/constants";
import { usePatchResumeSection } from "../../../../services/create-resume/queries";
import { RESUME_SECTIONS } from "../../../../services/create-resume/resumeSections";
import LinkInput from "./components/LinkInput";

const LINK_CATEGORIES = ["Website", "LinkedIn", "GitHub", "Twitter", "Portfolio", "Blog"];

const BLANK_LINK: PersonalLink = { type: "", value: "" };

interface PatchError {
	response: { data: { message: string | string[] } };
}

const PersonalLinks = () => {
	const [cookies] = useCookies(["resume"]);

	const navigate = useNavigate();

	const staticValue = new StaticValue();
	const patchSection = usePatchResumeSection();
	const staticValues = staticValue.getDefaultValues();
	const [serverErrors, setServerErrors] = useState<string[]>([]);

	const { resumeData, setResumeData } = useContext(ResumeContext) as ResumeContextValue;

	const [linkData, setLinkData] = useState<PersonalLink[]>([BLANK_LINK]);
	const [rowErrors, setRowErrors] = useState<string[]>([]);

	//form submition handler
	const addPersonalLinks = () => {
		const nonBlankLinks = linkData.filter((link) => link.type.trim() !== "" || link.value.trim() !== "");
		const results = nonBlankLinks.map((link) => personalLinkRowSchema.safeParse(link));
		const errors = results.map((result) => (result.success ? "" : "Enter a valid URL (https://...)"));
		setRowErrors(
			linkData.map((link) => {
				const nonBlankIndex = nonBlankLinks.indexOf(link);
				return nonBlankIndex === -1 ? "" : errors[nonBlankIndex];
			}),
		);
		if (results.some((result) => !result.success)) return;

		const payload = { data: nonBlankLinks, applicable: true };

		const resumeId = cookies[staticValues.localStorageResumeId];
		setResumeData((previous) => ({ ...previous, extras: { ...previous.extras, personalLinks: nonBlankLinks } }));
		patchSection.mutate({ resumeId, section: RESUME_SECTIONS.LINKS, data: payload } as never, { onSuccess, onError });
	};

	function onSuccess(_ledger: Record<string, unknown>) {
		navigate("/resume/extras");
	}

	function onError(error: Error) {
		const serverResponse = (error as unknown as PatchError).response.data;
		Array.isArray(serverResponse.message)
			? setServerErrors((prev) => {
					return [...prev, ...serverResponse.message];
				})
			: setServerErrors((prev) => {
					return [...prev, serverResponse.message as string];
				});
	}

	const setLinks = (index: number, data: PersonalLink) => {
		const newLinks = linkData.map((item, i) => (i === index ? data : item));
		setLinkData(newLinks);
		setRowErrors((previous) => previous.map((error, i) => (i === index ? "" : error)));
	};

	// With nothing saved the reader used to land on a bare "Add Link" CTA — a
	// screen that asks what to link to but offers nowhere to type it. Open a row for
	// the first link and let the CTA add the ones after it.
	useEffect(() => {
		const links = resumeData.extras.personalLinks;
		setLinkData(links.length > 0 ? links : [BLANK_LINK]);
	}, [resumeData.extras.personalLinks]);

	const addNewLink = () => {
		setLinkData([...linkData, BLANK_LINK]);
	};

	return (
		<div className="min-h-screen">
			{serverErrors.map((item, index) => {
				return <ErrorToast error={item} key={index} />;
			})}
			<div className="w-full md:max-w-2xl">
				<div className="mb-3">
					<h2 className="mb-3 font-bold text-2xl text-gray-900">What do you want to link to?</h2>
					<div className="flex items-start justify-between gap-3">
						<p className="text-gray-700">Add your website, portfolio or professional profiles.</p>
						<Tips content={tips.personalLinks} />
					</div>
				</div>

				{linkData.map((link, index) => (
					<LinkInput
						key={index}
						index={index}
						data={link}
						setData={setLinks}
						linkOptions={LINK_CATEGORIES}
						deleteLink={() => {
							const newLinks = linkData.filter((_item, i) => i !== index);
							// Deleting the last row would leave the reader back on a bare CTA.
							setLinkData(newLinks.length > 0 ? newLinks : [BLANK_LINK]);
						}}
						error={rowErrors[index]}
					/>
				))}
				<button
					type="button"
					className="mb-3 flex cursor-pointer items-center gap-2 font-semibold text-primary-700"
					onClick={addNewLink}
				>
					<Plus className="size-4" /> Add Link
				</button>

				<div className="flex items-center justify-end gap-2">
					<Button
						type="button"
						variant="link"
						onClick={() => {
							navigate("/resume/extras");
						}}
					>
						Skip for now
					</Button>
					<Button type="button" onClick={addPersonalLinks}>
						{patchSection.isPending ? (
							<>
								Loading <Spinner />
							</>
						) : (
							"Save"
						)}
					</Button>
				</div>
			</div>
		</div>
	);
};

export default PersonalLinks;
